import React, { useMemo, useRef, useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import Modal from 'react-bootstrap/Modal';
import moment from 'moment';
import { BiChevronsDown, BiChevronsUp, BiDisc } from 'react-icons/bi';
import { FiCalendar, FiFileText, FiUser } from 'react-icons/fi';
import { HiLink } from 'react-icons/hi';
import { AiOutlinePlus } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';

import ImageView from 'Components/Image/ImageView';
import KanbanDropDown from 'Components/KanbanCard/KanbanDropDown';
import KanbanList from 'Containers/KanbanList/KanbanList';
import { formattedDate, getUserImage, titleize } from 'helpers';
import useOnClickOutside from 'hooks/useOutsideClick';
import CardStyles from '../KanbanCard/KanbanCard.module.scss';
import './Calender.scss';
import NewDocumentModal from 'Components/Modals/NewDocumentModal/NewDocumentModal';
import { ROUTES } from 'constants/routes';
import UpgradeModal from 'Containers/Modal/UpgradeModal/UpgradeModal';
import { useSelector } from 'react-redux';
import { CONTENT_TYPE_ICONS } from 'constants/contentTypes';
import { ReactComponent as Other } from '../../assets/svg/Group.svg';
import WeeklyView from 'Components/WeeklyView/WeeklyView';

function ViewMoreModal({
  show,
  setShow,
  tasks,
  onMove,
  onEdit,
  onDuplicate,
  onDelete,
  onArchive,
  onUpdateBrief,
  handleAICreate,
}) {
  const handleClose = () => setShow(false);
  const priorities = ['low', 'medium', 'high'];

  const sortedTasks = tasks.sort(
    (a, b) => priorities.indexOf(b.priority) - priorities.indexOf(a.priority),
  );

  return (
    <Modal
      size="xl"
      centered
      show={show}
      onHide={handleClose}
      style={{ zIndex: 1050 }}
    >
      <Modal.Header className="border-0 pb-0 bg-black" closeButton>
        <Modal.Title className="Modal-Title">
          {tasks?.[0]?.deadline_date} - Tasks
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-0">
        <KanbanList
          tasks={sortedTasks}
          onDelete={onDelete}
          onMove={onMove}
          onEdit={onEdit}
          onArchive={onArchive}
          calendarTable={true}
          onUpdateBrief={onUpdateBrief}
          handleAICreate={handleAICreate}
        />
      </Modal.Body>
    </Modal>
  );
}

function Calender({
  tasks,
  onMove,
  onEdit,
  onDuplicate,
  onDelete,
  fetchCPTasks,
  onArchive,
  calendarProps = {},
  onUpdateBrief,
  handleAICreate,
  calendarView = 'month', // 'week' or 'month'
  onCreatePost, // Callback for creating new post with specific date
  onQuickUpdateDeadline, // Quick update without opening drawer
  currentDate = new Date(), // Reference date for calendar navigation
}) {
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedEventInfo, setSelectedEventInfo] = useState(null);
  const [show, setShow] = useState(false);
  const [moreDate, setMoreDate] = useState('');
  const [position, setPosition] = useState(null);
  const [NewDocumentModalView, setNewDocumentModalView] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskName, setTaskName] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const eventRef = useRef();
  const containerRef = useRef();
  const currentPeriodRef = useRef();

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const { id: userId, subscriptions } = user || {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};

  const navigate = useNavigate();

  useOnClickOutside(eventRef, () => {
    setSelectedEvent('');
    setPosition(null);
  });

  const handleEventClick1 = ({ event, jsEvent }) => {
    if (event.id === selectedEvent) {
      return;
    }
    setSelectedEventInfo(event);
    setSelectedEvent(event.id);

    setPosition({ top: jsEvent.clientY - 10, left: jsEvent.clientX - 40 });
  };

  function eventAddStyles(arg) {
    // all self-created props are under "extendedProps"
    if (arg.event.extendedProps.demanding === 'InReview') {
      return ['event-vocable-calender-Inreview'];
    } else if (arg.event.extendedProps.demanding === 'InProgress') {
      return ['event-vocable-calender-Inprogress'];
    } else if (arg.event.extendedProps.demanding === 'Approved') {
      return ['event-vocable-calender-Approved'];
    } else if (arg.event.extendedProps.demanding === 'Published') {
      return ['event-vocable-calender-Published'];
    } else {
      return ['event-vocable-calender-NotStarted'];
    }
  }

  const events = useMemo(() => {
    if (!tasks?.length) {
      return [];
    }

    return tasks.map((task) => {
      return {
        id: task.id,
        title: task.title,
        start: new Date(task.deadline_date).toISOString(),
        demanding: titleize(task.state, false),
        state: task.state,
        access: task.project_access,
        content_type: task.content_type,
      };
    });
  }, [tasks]);

  const handleClick = (id, templateId) => {
    if (templateId) {
      navigate(ROUTES.EditorDocumentTemplate(id, templateId));
    } else {
      navigate(ROUTES.FreestyleDocumentTemplate(id));
    }
  };

  const onHideModal = (refetchTasks = false) => {
    refetchTasks && fetchCPTasks && fetchCPTasks();
    setNewDocumentModalView(false);
  };

  const selectedTaskInfo =
    tasks.find((task) => Number(task.id) === Number(selectedEvent)) || {};

  const {
    title: task_title,
    state,
    assignee,
    deadline_date,
    priority,
    document,
    id: task_id,
    project_id,
  } = selectedTaskInfo || {};

  const TasksData = [
    {
      id: Math.random(),
      Heading: 'Status',
      info: titleize(state || ''),
    },
    {
      id: Math.random(),
      Heading: 'Assignee',
      info: assignee?.name || '',
      AssigneeAvatar: true,
      AssigneeAvatarSrc: assignee?.mini_avatar,
    },
    {
      id: Math.random(),
      Heading: 'Due Date',
      DueDate: deadline_date,
    },
    {
      id: Math.random(),
      Heading: 'Label',
      info: titleize(priority || ''),
    },
  ];

  const Tasks = TasksData.map((task) => {
    return (
      <div className="d-flex g-10" id={task.id} key={task.id}>
        <span className={CardStyles.CardCalenderEvent__TaskDetails_Heading}>
          {task.Heading}:
        </span>

        {task.AssigneeAvatar ? (
          <div
            className={`${CardStyles.CardCalenderEvent__TaskDetails_Info} d-flex`}
          >
            {task.AssigneeAvatarSrc ? (
              <ImageView
                class="event-card-avatar"
                src={task.AssigneeAvatarSrc}
              />
            ) : (
              getUserImage(
                task.AssigneeAvatarSrc,
                task.info,
                {
                  width: 18,
                  height: 18,
                },
                undefined,
                'event-card-avatar',
              )
            )}
            {task.info}
          </div>
        ) : task.Heading === 'Label' ? (
          <span className={`badge-wrapper ${task.info}Badge`}>
            {task.info !== 'High' ? <BiChevronsDown /> : <BiChevronsUp />}
            <span>{task.info}</span>
          </span>
        ) : task.Heading === 'Due Date' ? (
          <div
            className={`${CardStyles.CardCalenderEvent__TaskDetails_Info} d-flex`}
          >
            <FiCalendar color="#98A2B3" size={12} />
            {task.DueDate}
          </div>
        ) : (
          <span className={CardStyles.CardCalenderEvent__TaskDetails_Info}>
            {task.info}
          </span>
        )}
      </div>
    );
  });

  const renderEventContent = (eventInfo) => {
    const contentType = eventInfo.event.extendedProps.content_type;
    const icon = CONTENT_TYPE_ICONS[contentType] || <Other />;

    return (
      <div id={eventInfo.event.id} className="event_info">
        <div className="event-content-wrapper">
          <div className="event-content-icon">
            {icon}
          </div>
          <div className={CardStyles.CardCalenderEvent__Title}>
            {eventInfo.event.title}
          </div>
        </div>
      </div>
    );
  };

  const isYGreater = position?.top + 204 > window.innerHeight;
  const isXGreater = position?.left + 357 > window.innerWidth;

  const newX = isXGreater ? position?.left - 357 : position?.left;
  const newY = isYGreater ? position?.top - 204 : position?.top;

  const selectedEventTitleSize = selectedEventInfo?.title?.length || 0;

  // Generate array of time periods to display based on view
  const periodsToDisplay = useMemo(() => {
    const periods = [];
    const referenceDate = new Date(currentDate);

    if (calendarView === 'week') {
      // For week view, show past 4 weeks + current week + next 8 weeks = 13 weeks
      for (let i = -4; i <= 8; i++) {
        const weekStart = new Date(referenceDate);
        weekStart.setDate(referenceDate.getDate() + (i * 7));
        // Set to Monday of that week
        const day = weekStart.getDay();
        const diff = weekStart.getDate() - day + (day === 0 ? -6 : 1);
        weekStart.setDate(diff);
        periods.push(weekStart);
      }
    } else {
      // For month view, show past 3 months + current month + next 8 months = 12 months
      for (let i = -3; i <= 8; i++) {
        const monthDate = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + i, 1);
        periods.push(monthDate);
      }
    }
    return periods;
  }, [calendarView, currentDate]);

  // Filter events by period for each calendar instance
  const getEventsForPeriod = (periodDate) => {
    let startDate, endDate;

    if (calendarView === 'week') {
      // Week view: from Monday to Sunday
      startDate = new Date(periodDate);
      endDate = new Date(periodDate);
      endDate.setDate(endDate.getDate() + 6);
    } else {
      // Month view
      startDate = new Date(periodDate.getFullYear(), periodDate.getMonth(), 1);
      endDate = new Date(periodDate.getFullYear(), periodDate.getMonth() + 1, 0);
    }

    return events.filter(event => {
      const eventDate = new Date(event.start);
      return eventDate >= startDate && eventDate <= endDate;
    });
  };

  // Get separator label for period
  const getPeriodLabel = (periodDate) => {
    if (calendarView === 'week') {
      const endDate = new Date(periodDate);
      endDate.setDate(endDate.getDate() + 6);

      const startMonth = periodDate.toLocaleString('default', { month: 'long' });
      const endMonth = endDate.toLocaleString('default', { month: 'long' });
      const year = periodDate.getFullYear();

      if (startMonth === endMonth) {
        return `${startMonth} ${periodDate.getDate()}-${endDate.getDate()}, ${year}`;
      } else {
        return `${startMonth} ${periodDate.getDate()} - ${endMonth} ${endDate.getDate()}, ${year}`;
      }
    } else {
      return periodDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    }
  };

  // Calculate which index is the current period (month or week)
  const currentPeriodIndex = calendarView === 'week' ? 4 : 3;

  // Scroll to current period on mount and when date changes
  useEffect(() => {
    if (currentPeriodRef.current) {
      // Scroll so divider appears at the top of viewport
      currentPeriodRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [calendarView, currentDate]);

  console.log('window.innerWidth', window.innerWidth > 758);

  // Extract ref from calendarProps if present
  const { ref: calendarPropsRef, ...otherCalendarProps } = calendarProps;

  // Use callback ref to handle both refs
  useEffect(() => {
    if (calendarPropsRef) {
      if (typeof calendarPropsRef === 'function') {
        calendarPropsRef(containerRef.current);
      } else if (calendarPropsRef) {
        calendarPropsRef.current = containerRef.current;
      }
    }
  }, [calendarPropsRef]);

  return (
    <>
      <section ref={containerRef} className="vocable-calender vocable-calender-scrollable">
        {periodsToDisplay.map((periodDate, index) => (
          <div
            key={index}
            className="calendar-month-container"
            data-period-date={periodDate.toISOString()}
            ref={index === currentPeriodIndex ? currentPeriodRef : null}
          >
            {index > 0 && (
              <div className="calendar-month-separator">
                <span className="calendar-month-separator-text">
                  {getPeriodLabel(periodDate)}
                </span>
              </div>
            )}

            {calendarView === 'week' ? (
              <WeeklyView
                key={periodDate.toISOString()}
                tasks={tasks || []}
                weekStartDate={periodDate}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                onMove={onMove}
                onArchive={onArchive}
                onCreatePost={onCreatePost}
                onQuickUpdateDeadline={onQuickUpdateDeadline}
                handleEventClick={(task) => {
                  setSelectedEvent(task.id);
                  setSelectedEventInfo({
                    id: task.id,
                    title: task.title,
                    extendedProps: {
                      access: task.project_access,
                      state: task.state,
                    },
                  });
                }}
                access={selectedTaskInfo?.project_access}
              />
            ) : (
              <FullCalendar
                key={periodDate.toISOString()}
                eventClassNames={eventAddStyles}
                events={getEventsForPeriod(periodDate)}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                initialDate={periodDate}
                contentHeight="auto"
                height="auto"
                firstDay={1}
                editable={true}
                eventClick={handleEventClick1}
                eventDrop={(info) => {
                  // Handle event drop - update deadline
                  if (onQuickUpdateDeadline) {
                    const taskId = info.event.id;
                    // Use UTC values to avoid timezone conversion issues
                    const startDate = info.event.start;
                    const newDate = new Date(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate());
                    onQuickUpdateDeadline(taskId, newDate);
                  }
                }}
                moreLinkClick={(data) => {
                  setShow(true);
                  setMoreDate(data.date);
                }}
                eventContent={renderEventContent}
                dayMaxEvents={2}
                headerToolbar={index === 0 ? {
                  left: 'prev,next today',
                  center: 'title',
                  right: '',
                } : false}
                {...(index === 0 ? otherCalendarProps : {})}
              />
            )}
          </div>
        ))}

        {show && (
          <ViewMoreModal
            show={show}
            setShow={setShow}
            tasks={tasks.filter(
              (task) =>
                formattedDate(new Date(task.deadline_date)) ===
                formattedDate(new Date(moreDate)),
            )}
            onDelete={onDelete}
            onEdit={onEdit}
            onMove={onMove}
            onArchive={onArchive}
            onUpdateBrief={onUpdateBrief}
            handleAICreate={handleAICreate}
          />
        )}

        {position && (
          <section
            className={CardStyles.CardCalenderEventClean}
            style={{
              top: newY,
              left: window.innerWidth > 758 ? newX : 0,
            }}
            ref={eventRef}
          >
            {/* Header: Content type badge + Dropdown */}
            <div className={CardStyles.CardCalenderEventClean__Header}>
              <div className={CardStyles.CardCalenderEventClean__ContentType}>
                {selectedTaskInfo?.content_type && CONTENT_TYPE_ICONS[selectedTaskInfo.content_type] ? (
                  <div className={CardStyles.ContentTypeIcon}>
                    {CONTENT_TYPE_ICONS[selectedTaskInfo.content_type]}
                  </div>
                ) : null}
                <span>{selectedTaskInfo?.content_type || 'Content'}</span>
              </div>
              <KanbanDropDown
                id={Number(selectedEventInfo?.id)}
                onDelete={(value) => {
                  setPosition(null);
                  setSelectedEvent('');
                  onDelete(value);
                }}
                onEdit={(value) => {
                  setPosition(null);
                  setSelectedEvent('');
                  onEdit(value);
                }}
                onMove={(value) => {
                  setPosition(null);
                  setSelectedEvent('');
                  onMove(value);
                }}
                onArchive={(value) => {
                  setPosition(null);
                  setSelectedEvent('');
                  onArchive(value);
                }}
                access={selectedEventInfo?.extendedProps?.access}
                state={selectedEventInfo?.extendedProps?.state}
              />
            </div>

            {/* Content section with title, status, and date */}
            <div className={CardStyles.CardCalenderEventClean__Content}>
              <div className={CardStyles.CardCalenderEventClean__TitleSection}>
                <h3 className={CardStyles.CardCalenderEventClean__Title}>
                  {selectedEventInfo?.title}
                </h3>

                {/* Status Badge */}
                {state && (
                  <div className={`${CardStyles.CardCalenderEventClean__Status} ${CardStyles['status-' + state.replace(/_/g, '-')]}`}>
                    {titleize(state)}
                  </div>
                )}

                {/* Deadline */}
                {deadline_date && (
                  <div className={CardStyles.CardCalenderEventClean__Deadline}>
                    <FiCalendar size={16} />
                    <span>{moment(deadline_date).format('MM/DD/YYYY')}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Footer: Assignee + Action button */}
            <div className={CardStyles.CardCalenderEventClean__Footer}>
              {assignee?.name && (
                <div className={CardStyles.CardCalenderEventClean__Assignee}>
                  <div className={CardStyles.AssigneeAvatar}>
                    {assignee.mini_avatar ? (
                      <ImageView src={assignee.mini_avatar} />
                    ) : (
                      <FiUser size={12} />
                    )}
                  </div>
                  <span>{assignee.name}</span>
                </div>
              )}

              {document?.id ? (
                <button
                  className={CardStyles.CardCalenderEventClean__Button}
                  onClick={() => handleClick(document?.id, document?.template_id)}
                >
                  Open Draft
                </button>
              ) : (
                <button
                  className={CardStyles.CardCalenderEventClean__Button}
                  onClick={() => {
                    handleAICreate(task_id, project_id, task_title);
                  }}
                >
                  View Content
                </button>
              )}
            </div>
          </section>
        )}
      </section>
      {showUpgradeModal && (
        <UpgradeModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
          message={
            'Updating campaign related content is a pro feature. Please update to pro plan to access this feature.'
          }
        />
      )}

      {!!taskId && (
        <NewDocumentModal
          show={NewDocumentModalView}
          onHide={onHideModal}
          taskId={taskId}
          taskName={taskName}
          projectId={projectId}
          aiPowered
        />
      )}
    </>
  );
}

export default Calender;
